import { z } from "zod";
import { idSchema, instantSchema, reasonSchema } from "@amber/contracts";
import { languageCodeSchema, profileLifecycleStateSchema } from "./records.js";
import { planHistorySchema } from "./plans.js";

export const ONBOARDING_ACTIONS = {
  create: "profiles.create",
  startTracking: "profiles.start_tracking",
  decideSuggestion: "profiles.decide_suggestion",
} as const;

export const EDITOR_PROFILES_REASON = "Only Editors and above can create profiles and start tracking";

export const WIKIPEDIA_EDITION = /^[a-z]{2,3}(-[a-z]+)?$/;
export const editionSchema = z.string().regex(WIKIPEDIA_EDITION, "expected a Wikipedia language edition");
export const wikidataIdSchema = z.string().regex(/^Q\d+$/, "expected a Wikidata ID");

export function parseWikipediaUrl(input: string): { edition: string; title: string } | null {
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(input.trim()) ? input.trim() : `https://${input.trim()}`);
  } catch {
    return null;
  }
  const host = /^([a-z]{2,3}(?:-[a-z]+)?)(?:\.m)?\.wikipedia\.org$/i.exec(url.hostname);
  if (!host?.[1]) return null;
  const fromPath = /^\/wiki\/(.+)$/.exec(url.pathname)?.[1];
  const raw = fromPath ?? url.searchParams.get("title");
  if (!raw) return null;
  let title: string;
  try {
    title = decodeURIComponent(raw);
  } catch {
    title = raw;
  }
  title = title.replace(/_/g, " ").trim();
  return title.length > 0 ? { edition: host[1].toLowerCase(), title } : null;
}

export const wikipediaUrl = (edition: string, title: string) =>
  `https://${edition}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, "_"))}`;
export const wikidataUrl = (id: string) => `https://www.wikidata.org/wiki/${id}`;

export const candidateSchema = z.strictObject({
  wikidata_id: wikidataIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  current_role: z.string().nullable(),
  birth_date: z.string().nullable(),
  edition: editionSchema.nullable(),
  article_url: z.url().nullable(),
  image_url: z.url().nullable(),
  existing: z.strictObject({ id: idSchema("prf"), name: z.string(), state_label: z.string() }).nullable(),
});
export type Candidate = z.infer<typeof candidateSchema>;

export const candidatesResponseSchema = z.strictObject({
  query: z.string(),
  kind: z.enum(["name", "link"]),
  candidates: z.array(candidateSchema),
  link_error: z.string().nullable(),
});
export type CandidatesResponse = z.infer<typeof candidatesResponseSchema>;

export const FIELD_STATUSES = ["sourced", "ai_inferred", "editor", "missing", "conflict"] as const;
export const fieldStatusSchema = z.enum(FIELD_STATUSES);
export const FIELD_STATUS_LABELS: Record<z.infer<typeof fieldStatusSchema>, string> = {
  sourced: "Sourced",
  ai_inferred: "AI inference",
  editor: "Entered by an Editor",
  missing: "Missing",
  conflict: "Conflicting",
};

export const IDENTITY_FIELDS = [
  "name",
  "current_role",
  "affiliation",
  "birth_date",
  "aliases",
  "photo",
  "description",
] as const;
export const identityFieldSchema = z.enum(IDENTITY_FIELDS);
export type IdentityField = z.infer<typeof identityFieldSchema>;
export const IDENTITY_FIELD_LABELS: Record<IdentityField, string> = {
  name: "Name",
  current_role: "Current role",
  affiliation: "Affiliation",
  birth_date: "Birth date",
  aliases: "Aliases",
  photo: "Photo",
  description: "Description",
};
export const OPTIONAL_FIELDS = ["current_role", "affiliation", "birth_date", "aliases", "photo"] as const;
export type OptionalField = (typeof OPTIONAL_FIELDS)[number];

export const proposedFieldSchema = z.strictObject({
  field: identityFieldSchema,
  value: z.string().nullable(),
  values: z.array(z.strictObject({ value: z.string(), evidence: z.string() })),
  evidence: z.string().nullable(),
  evidence_url: z.url().nullable(),
  status: fieldStatusSchema,
});
export type ProposedField = z.infer<typeof proposedFieldSchema>;

export const editionReadSchema = z.strictObject({
  edition: editionSchema,
  title: z.string().nullable(),
  url: z.url().nullable(),
  state: z.enum(["read", "missing", "error"]),
  detail: z.string().nullable(),
});
export type EditionRead = z.infer<typeof editionReadSchema>;

export const openQuestionSchema = z.strictObject({
  field: identityFieldSchema.or(z.literal("article")),
  kind: z.enum(["missing", "conflict", "unreadable"]),
  detail: z.string(),
});
export type OpenQuestion = z.infer<typeof openQuestionSchema>;

export const accountSchema = z.strictObject({
  platform: z.string(),
  handle: z.string(),
  url: z.url(),
  property: z.string(),
  ended: z.string().nullable(),
  rank: z.enum(["preferred", "normal", "deprecated"]),
  evidence_url: z.url(),
});
export type ProfileAccount = z.infer<typeof accountSchema>;

export const duplicateSchema = z.strictObject({
  id: idSchema("prf"),
  name: z.string(),
  state_label: z.string(),
  role: z.string().nullable(),
  reason: z.string(),
  same_identity: z.boolean(),
  can_complete: z.boolean(),
  revision: z.number().int().positive(),
});
export type Duplicate = z.infer<typeof duplicateSchema>;

export const identityProposalSchema = z.strictObject({
  wikidata_id: wikidataIdSchema,
  wikidata_url: z.url(),
  source: z.strictObject({ edition: editionSchema, title: z.string(), url: z.url() }),
  name: z.string(),
  image_url: z.url().nullable(),
  linked_editions: z.array(editionSchema),
  editions: z.array(editionReadSchema),
  fields: z.array(proposedFieldSchema),
  open_questions: z.array(openQuestionSchema),
  languages: z.array(languageCodeSchema),
  accounts: z.array(accountSchema),
  duplicates: z.array(duplicateSchema),
});
export type IdentityProposal = z.infer<typeof identityProposalSchema>;

export const proposalRequestSchema = z
  .strictObject({
    wikidata_id: wikidataIdSchema.optional(),
    url: z.string().trim().min(1).optional(),
  })
  .refine((r) => r.wikidata_id !== undefined || r.url !== undefined, "Choose a candidate or paste a Wikipedia link");

const acceptSchema = z.strictObject({
  current_role: z.boolean().default(true),
  affiliation: z.boolean().default(true),
  birth_date: z.boolean().default(true),
  aliases: z.boolean().default(true),
  photo: z.boolean().default(true),
});

const duplicateDecisionSchema = z.array(idSchema("prf")).max(20).default([]);

export const createProfileRequestSchema = z.discriminatedUnion("mode", [
  z.strictObject({
    mode: z.literal("wikipedia"),
    wikidata_id: wikidataIdSchema,
    source_url: z.url(),
    display_name: z.string().trim().min(1, "Enter the display name").max(200),
    languages: z.array(languageCodeSchema).min(1, "Choose at least one language to monitor"),
    rationale: z.string().trim().max(2000).optional(),
    accept: acceptSchema.default({
      current_role: true,
      affiliation: true,
      birth_date: true,
      aliases: true,
      photo: true,
    }),
    different_from: duplicateDecisionSchema,
  }),
  z.strictObject({
    mode: z.literal("manual"),
    display_name: z.string().trim().min(1, "Enter the display name").max(200),
    languages: z.array(languageCodeSchema).min(1, "Choose at least one language to monitor"),
    rationale: z.string().trim().min(1, "Say why this person should be tracked").max(2000),
    current_role: z.string().trim().max(300).optional(),
    different_from: duplicateDecisionSchema,
  }),
]);
export type CreateProfileRequest = z.infer<typeof createProfileRequestSchema>;

export const completeIdentityRequestSchema = z.strictObject({
  wikidata_id: wikidataIdSchema,
  source_url: z.url(),
  base_revision: z.number().int().positive(),
  accept: acceptSchema.default({ current_role: true, affiliation: true, birth_date: true, aliases: true, photo: true }),
  reason: reasonSchema,
});

export const DISCOVERY_STATES = ["none", "queued", "finding", "ready", "failed"] as const;
export const discoveryStateSchema = z.enum(DISCOVERY_STATES);

export const SUGGESTION_RELATIONSHIPS = [
  "own",
  "office",
  "organisation",
  "host",
  "guest",
  "official_record",
  "coverage",
  "unverified_lead",
] as const;
export const suggestionRelationshipSchema = z.enum(SUGGESTION_RELATIONSHIPS);
export type SuggestionRelationship = z.infer<typeof suggestionRelationshipSchema>;
export const SUGGESTION_RELATIONSHIP_LABELS: Record<SuggestionRelationship, string> = {
  own: "Own",
  office: "Office",
  organisation: "Organisation",
  host: "Host",
  guest: "Guest",
  official_record: "Official record",
  coverage: "Coverage",
  unverified_lead: "Unverified lead",
};
export const SUGGESTION_GROUPINGS = ["own_strong", "own_weak", "known_account", "shared"] as const;
export const suggestionGroupingSchema = z.enum(SUGGESTION_GROUPINGS);
export type SuggestionGrouping = z.infer<typeof suggestionGroupingSchema>;
export const suggestionStateSchema = z.enum(["open", "approved", "rejected", "discarded"]);

export const candidateSourceSchema = z.strictObject({ platform: z.string(), url: z.url(), title: z.string() });
export type CandidateSource = z.infer<typeof candidateSourceSchema>;

export const suggestionViewSchema = z.strictObject({
  id: idSchema("sug"),
  grouping: suggestionGroupingSchema,
  source_id: idSchema("src").nullable(),
  title: z.string(),
  url: z.string(),
  platform: z.string(),
  platform_label: z.string(),
  platform_code: z.string(),
  collected: z.boolean(),
  relationship: suggestionRelationshipSchema,
  why: z.string(),
  warning: z.string().nullable(),
  evidence_url: z.string().nullable(),
  preselected: z.boolean(),
  state: suggestionStateSchema,
  decided_by: z.string().nullable(),
  decided_at: instantSchema.nullable(),
  reason: z.string().nullable(),
  approved_source_id: idSchema("src").nullable(),
});
export type SuggestionView = z.infer<typeof suggestionViewSchema>;

export const coveredSourceSchema = z.strictObject({
  source_id: idSchema("src"),
  title: z.string(),
  platform_code: z.string(),
  platform_label: z.string(),
  why: z.string(),
  monitored_for: z.array(z.string()),
});
export type CoveredSource = z.infer<typeof coveredSourceSchema>;

/** A source the person already approved, outside every plan, that Start tracking can include without a new approval. */
export const approvedSourceSchema = z.strictObject({
  source_id: idSchema("src"),
  title: z.string(),
  platform_code: z.string(),
  platform_label: z.string(),
  relationship: suggestionRelationshipSchema,
});
export type ApprovedSource = z.infer<typeof approvedSourceSchema>;

export const firstPlanSchema = z.strictObject({
  discovery: z.strictObject({
    state: discoveryStateSchema,
    at: instantSchema.nullable(),
    detail: z.string().nullable(),
  }),
  preselected: z.array(suggestionViewSchema),
  approved: z.array(approvedSourceSchema),
  covered: z.array(coveredSourceSchema),
  suggestions: z.array(suggestionViewSchema),
  decided: z.array(suggestionViewSchema),
  recheck_estimate: z.number().int().nonnegative(),
  back_to: z.iso.date(),
});
export type FirstPlan = z.infer<typeof firstPlanSchema>;

export const startTrackingRequestSchema = z.strictObject({
  selected: z.array(idSchema("sug")).max(100),
  sources: z.array(idSchema("src")).max(100).default([]),
  history: planHistorySchema.default({ option: "default", since: null }),
  reason: reasonSchema,
});
export type StartTrackingRequest = z.infer<typeof startTrackingRequestSchema>;

export const decideSuggestionRequestSchema = z.discriminatedUnion("decision", [
  z.strictObject({ decision: z.literal("approve"), reason: z.string().trim().max(2000).optional() }),
  z.strictObject({ decision: z.literal("reject"), reason: reasonSchema }),
]);

export const recheckViewSchema = z.strictObject({
  id: idSchema("rck"),
  state: z.enum(["queued", "running", "finished"]),
  trigger: z.enum(["first_plan_applied", "reactivated"]),
  items_total: z.number().int().nonnegative().nullable(),
  items_checked: z.number().int().nonnegative(),
  found: z.number().int().nonnegative(),
  found_excluded: z.number().int().nonnegative(),
  back_to: z.iso.date(),
  started_at: instantSchema.nullable(),
  finished_at: instantSchema.nullable(),
});
export type RecheckView = z.infer<typeof recheckViewSchema>;

export const identityViewSchema = z.strictObject({
  fields: z.array(proposedFieldSchema),
  open_questions: z.array(openQuestionSchema),
  editions: z.array(editionReadSchema),
  wikidata_id: wikidataIdSchema.nullable(),
  wikidata_url: z.url().nullable(),
  identity_confidence: z.number().nullable(),
  last_verified_at: instantSchema.nullable(),
  stale: z.boolean(),
  accounts: z.array(accountSchema),
  picture: z.strictObject({ url: z.url(), origin_url: z.url(), rights: z.string() }).nullable(),
});

export const profileDetailBaseSchema = z.strictObject({
  id: idSchema("prf"),
  name: z.string(),
  lifecycle_state: profileLifecycleStateSchema,
  state_label: z.string(),
  role: z.string().nullable(),
  affiliation: z.string().nullable(),
  languages: z.array(languageCodeSchema),
  owner: z.string().nullable(),
  rationale: z.string().nullable(),
  created_by: z.string(),
  created_at: instantSchema,
  created_from: z.string(),
  revision: z.number().int().positive(),
  restricted: z.boolean(),
  identity: identityViewSchema,
  recheck: recheckViewSchema.nullable(),
  duplicates: z.array(duplicateSchema),
  appearance_threshold: z.number(),
});

export const appearanceRowSchema = z.strictObject({
  item_id: idSchema("itm"),
  title: z.string(),
  source: z.string().nullable(),
  platform: z.string(),
  published: z.string().nullable(),
  communications: z.number().int().positive(),
  confidence: z.number().nullable(),
  href: z.string(),
});
export type AppearanceRow = z.infer<typeof appearanceRowSchema>;

export const appearancesResponseSchema = z.strictObject({
  threshold: z.number(),
  count: z.number().int().nonnegative(),
  communications: z.number().int().nonnegative(),
  items: z.array(appearanceRowSchema),
  possible: z.array(appearanceRowSchema),
});
export type AppearancesResponse = z.infer<typeof appearancesResponseSchema>;
